const kimberleyCenter = { lat: -28.7383, lng: 24.7639 };

const knownCoordinates = {
  "ambassador-lodge": { lat: -28.7258, lng: 24.7738 },
  "batsumi-guest-lodge": { lat: -28.7667, lng: 24.7361 },
  "diamond-pavilion-mall": { lat: -28.7665, lng: 24.7589 },
  "inter-city": { lat: -28.7387, lng: 24.7627 },
  "kamfers-dam": { lat: -28.7307, lng: 24.8432 },
  "kimberley-mine-museum": { lat: -28.7381, lng: 24.7586 },
  "lyve-cafe": { lat: -28.7396, lng: 24.7638 },
  "magersfontein-battlefield": { lat: -28.9771, lng: 24.6939 },
  "mcgregor-museum": { lat: -28.7445, lng: 24.7906 },
  "mogregor-museum": { lat: -28.7445, lng: 24.7906 },
  "mokala-national-park": { lat: -29.1739, lng: 24.3302 },
  "rooipoort-nature-reserve": { lat: -28.5618, lng: 24.2762 },
  "shama-guest-house": { lat: -28.7368, lng: 24.7609 },
  "the-big-hole": { lat: -28.7381, lng: 24.7586 },
  "william-humphreys-art-gallery": { lat: -28.7439, lng: 24.7668 },
};

export const defaultSmartFilters = {
  search: "",
  category: "All",
  maxPrice: "any",
  minRating: "0",
  maxDistance: "any",
  openNow: false,
  familyFriendly: false,
  sortBy: "recommended",
};

export const normalizeKey = (value = "") =>
  String(value)
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

export const getNumericPrice = (item = {}) => {
  const value = item.pricePerNight || item.price || item.priceRange || item.fare || "";
  if (typeof value === "number") return value;

  const matches = String(value).match(/\d+(?:[.,]\d+)?/g);
  if (!matches?.length) return 0;

  return Math.max(...matches.map((match) => Number(match.replace(",", ".")) || 0));
};

export const getItemCoordinates = (item = {}) => {
  const candidates = [
    item.slug,
    item.id,
    item.name,
    item.title,
    item.location,
  ].filter(Boolean);

  for (const candidate of candidates) {
    const coordinates = knownCoordinates[normalizeKey(candidate)];
    if (coordinates) return coordinates;
  }

  return null;
};

export const getDistanceFromKimberley = (item = {}) => {
  const coordinates = getItemCoordinates(item);
  if (!coordinates) return null;

  const earthRadiusKm = 6371;
  const degreesToRadians = (degrees) => (degrees * Math.PI) / 180;
  const latDifference = degreesToRadians(coordinates.lat - kimberleyCenter.lat);
  const lngDifference = degreesToRadians(coordinates.lng - kimberleyCenter.lng);
  const startLat = degreesToRadians(kimberleyCenter.lat);
  const endLat = degreesToRadians(coordinates.lat);

  const haversine =
    Math.sin(latDifference / 2) ** 2 +
    Math.cos(startLat) * Math.cos(endLat) * Math.sin(lngDifference / 2) ** 2;

  return earthRadiusKm * 2 * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine));
};

export const getHoursText = (item = {}) =>
  item.openingHours || item.hours || item.operatingHours || item.scheduleText || "";

const getMinutes = (time = "") => {
  const match = String(time).match(/(\d{1,2})(?::|h)?(\d{2})?/i);
  if (!match) return null;

  const hours = Number(match[1]);
  const minutes = Number(match[2] || 0);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;

  return hours * 60 + minutes;
};

export const getOpenStatus = (item = {}, date = new Date()) => {
  const hoursText = getHoursText(item);
  const matches = String(hoursText).match(/\d{1,2}(?::|h)?\d{0,2}/g);

  if (!matches || matches.length < 2) {
    return {
      known: false,
      open: false,
      label: "Hours unavailable",
    };
  }

  const start = getMinutes(matches[0]);
  const end = getMinutes(matches[1]);
  if (start === null || end === null) {
    return {
      known: false,
      open: false,
      label: "Hours unavailable",
    };
  }

  const current = date.getHours() * 60 + date.getMinutes();
  const open = end < start ? current >= start || current <= end : current >= start && current <= end;

  return {
    known: true,
    open,
    label: open ? "Open now" : "Closed now",
  };
};

export const isFamilyFriendly = (item = {}) => {
  const searchableText = [
    item.name,
    item.title,
    item.description,
    item.category,
    ...(Array.isArray(item.amenities) ? item.amenities : []),
    ...(Array.isArray(item.shops) ? item.shops : []),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return /family|child|children|kids|wheelchair|parking|restroom|safe|accessible/.test(searchableText);
};

export const getUniqueCategories = (items = []) => {
  const categories = items
    .map((item) => item.category)
    .filter(Boolean)
    .map((category) => String(category).trim())
    .filter(Boolean);

  return ["All", ...Array.from(new Set(categories)).sort()];
};

export const filterSmartItems = (items = [], filters = defaultSmartFilters) => {
  const query = filters.search.trim().toLowerCase();
  const maxPrice = filters.maxPrice === "any" ? Infinity : Number(filters.maxPrice);
  const minRating = Number(filters.minRating) || 0;
  const maxDistance = filters.maxDistance === "any" ? Infinity : Number(filters.maxDistance);

  const filteredItems = items.filter((item) => {
    const searchableText = [
      item.name,
      item.title,
      item.description,
      item.location,
      item.category,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    const price = getNumericPrice(item);
    const rating = Number(item.rating) || 0;
    const distance = getDistanceFromKimberley(item);
    const openStatus = getOpenStatus(item);

    if (query && !searchableText.includes(query)) return false;
    if (filters.category !== "All" && item.category !== filters.category) return false;
    if (maxPrice !== Infinity && price > maxPrice) return false;
    if (rating < minRating) return false;
    if (maxDistance !== Infinity && (distance === null || distance > maxDistance)) return false;
    if (filters.openNow && (!openStatus.known || !openStatus.open)) return false;
    if (filters.familyFriendly && !isFamilyFriendly(item)) return false;

    return true;
  });

  return filteredItems.sort((a, b) => {
    if (filters.sortBy === "rating") return (Number(b.rating) || 0) - (Number(a.rating) || 0);
    if (filters.sortBy === "price") return getNumericPrice(a) - getNumericPrice(b);
    if (filters.sortBy === "distance") {
      return (getDistanceFromKimberley(a) ?? Infinity) - (getDistanceFromKimberley(b) ?? Infinity);
    }
    return (Number(b.rating) || 0) - (Number(a.rating) || 0);
  });
};
